/**
 * ConnectCrtsDialog
 *
 * Confirmation dialog for the "Conexión entre destinos" feature.
 * Shows the user exactly what data will be used to create the bridge CRT
 * before they confirm, and lets them swap which CRT is "first" vs "second".
 *
 * Derivation rules (as defined in spec):
 *   shipperCity  (origin)  = first CRT's recipientCity
 *   recipientCity (dest)   = second CRT's shipperCity
 *   driver / truck / trailer / date = taken from first CRT's detail
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ArrowRightIcon, ArrowLeftRightIcon } from "lucide-react"

// ─── Types ────────────────────────────────────────────────────────────────────

/** Minimal CRT data needed to render the preview. */
export interface ConnectCrtPreview {
  id: string
  crt: string
  shipperCity: string | null
  recipientCity: string | null
  collectionArrival?: string | null
}

interface Props {
  open: boolean
  onOpenChange: (v: boolean) => void
  /** The CRT whose recipientCity becomes the new origin. */
  firstCrt: ConnectCrtPreview
  /** The CRT whose shipperCity becomes the new destination. */
  secondCrt: ConnectCrtPreview
  /** Swap first ↔ second. */
  onSwap: () => void
  onConfirm: () => void
  isPending: boolean
  error: string | null
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CrtCard({
  label,
  crt,
  highlight,
}: {
  label: string
  crt: ConnectCrtPreview
  /** Which city field is highlighted as the relevant one. */
  highlight: "recipient" | "shipper"
}) {
  return (
    <div className="flex-1 rounded-md border bg-muted/40 p-3 space-y-1.5 min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="font-mono text-sm font-semibold truncate">{crt.crt}</p>
      <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
        <span className="text-muted-foreground">Origen:</span>
        <span className={highlight === "shipper" ? "font-semibold text-foreground" : "text-muted-foreground"}>
          {crt.shipperCity ?? <span className="italic">sin ciudad</span>}
        </span>
        <span className="text-muted-foreground">Destino:</span>
        <span className={highlight === "recipient" ? "font-semibold text-foreground" : "text-muted-foreground"}>
          {crt.recipientCity ?? <span className="italic">sin ciudad</span>}
        </span>
      </div>
    </div>
  )
}

function DerivedRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-x-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ConnectCrtsDialog({
  open,
  onOpenChange,
  firstCrt,
  secondCrt,
  onSwap,
  onConfirm,
  isPending,
  error,
}: Props) {
  const newOrigin = firstCrt.recipientCity ?? "—"
  const newDest   = secondCrt.shipperCity  ?? "—"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Conexión entre destinos</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          {/* Error banner */}
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">
              {error}
            </p>
          )}

          {/* CRT pair + swap button */}
          <div className="flex items-center gap-2">
            <CrtCard label="CRT más viejo (origen)" crt={firstCrt} highlight="recipient" />

            <div className="flex flex-col items-center gap-1 shrink-0">
              <ArrowRightIcon className="size-4 text-muted-foreground" />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={onSwap}
                disabled={isPending}
                title="Intercambiar el orden de los CRTs"
              >
                <ArrowLeftRightIcon className="size-3.5 mr-1" />
                Intercambiar
              </Button>
            </div>

            <CrtCard label="CRT más nuevo (destino)" crt={secondCrt} highlight="shipper" />
          </div>

          {/* Derived values preview */}
          <div className="rounded-md border bg-muted/20 p-3 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Nuevo CRT que se creará
            </p>
            <DerivedRow label="Código CRT"     value="Auto-generado (C…)" />
            <DerivedRow label="ID externo"     value="Auto-generado (V…)" />
            <DerivedRow label="Ciudad origen"  value={newOrigin} />
            <DerivedRow label="Ciudad destino" value={newDest} />
            <DerivedRow label="Cliente"        value="— (vacío)" />
            <DerivedRow label="Chofer / Camión / Carreta" value="Del primer detalle del CRT más viejo" />
            <DerivedRow label="Fecha de carga" value="Fecha del CRT 1 + 1 día" />
            <DerivedRow label="Exportación"    value="Solo datos de viaje (sin carga)" />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancelar
          </Button>
          <Button onClick={onConfirm} disabled={isPending}>
            {isPending ? "Creando..." : "Confirmar y crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
